import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Where each role should land when denied access. Roles without dashboard
// access must NOT be sent to '/' — that just re-denies and loops forever.
const ROLE_HOME = {
  'Sales Staff': '/welcome',
  'Accountant': '/accounting',
  'Used Car Manager': '/inventory',
  'Broker': '/broker',
};

export default function ProtectedRoute({ children, allowedRoles, page }) {
  const { employee, loading, hasRole, hasAccess } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return <Navigate to="/employee-login" replace />;
  }

  // Role-aware denied fallback. If the employee is already on their home path
  // and still denied, show a visible message instead of navigating again —
  // guaranteeing no redirect loop.
  const deniedFallback = () => {
    const home = ROLE_HOME[employee.role] || '/';
    if (location.pathname !== home) {
      return <Navigate to={home} replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  };

  // Check role-based access
  if (allowedRoles && !hasRole(allowedRoles)) {
    return deniedFallback();
  }

  // Check page-based access
  if (page && !hasAccess(page)) {
    return deniedFallback();
  }

  return children;
}
